import JSZip from "jszip";

// Small DOM-walker primitives shared across the XML parsers.
//
// All functions intentionally use `getElementsByTagNameNS("*", localName)` so
// they ignore XML namespaces — the portal exports we ingest mix unprefixed and
// `fa:` / `ns2:` prefixed elements depending on the schema.

export const xmlText = (parent: Element, localName: string): string => {
  const node = parent.getElementsByTagNameNS("*", localName)[0];
  return node?.textContent?.trim() ?? "";
};

export const xmlNumber = (parent: Element, localName: string): number => {
  const v = xmlText(parent, localName);
  if (!v) return 0;
  const n = Number(v.replace(/,/g, "."));
  return Number.isFinite(n) ? n : 0;
};

export const directChildElements = (parent: Element, localName: string): Element[] => {
  const out: Element[] = [];
  for (let i = 0; i < parent.childNodes.length; i += 1) {
    const node = parent.childNodes[i];
    if (node.nodeType === 1 && (node as Element).localName === localName) {
      out.push(node as Element);
    }
  }
  return out;
};

export const parseXml = (xmlString: string): { doc: Document; error?: string } => {
  const doc = new DOMParser().parseFromString(xmlString, "text/xml");
  const err = doc.getElementsByTagName("parsererror")[0];
  return { doc, error: err ? err.textContent?.trim() || "XML parser error" : undefined };
};

// Looks for the application year (Antragsjahr / Wirtschaftsjahr / Jahr) in an
// XML document. Walks each candidate element name, takes the first value that
// looks like a 4-digit year in a sane range, and returns it. `parent` defaults
// to the document element so callers can pass either the full doc or a
// scoped element (e.g. <flaechenantrag>).
export const YEAR_ELEMENT_CANDIDATES = ["antragsjahr", "wirtschaftsjahr", "jahr", "saison"] as const;

const MIN_YEAR = 2015;
const MAX_YEAR = new Date().getFullYear() + 1;

const normaliseYear = (raw: string): number | undefined => {
  if (!raw) return undefined;
  const m = raw.match(/(?<![0-9])(20[1-3]\d)(?![0-9])/);
  if (!m) return undefined;
  const n = Number(m[1]);
  return n >= MIN_YEAR && n <= MAX_YEAR ? n : undefined;
};

export const findYearInXml = (root: Document | Element): number | undefined => {
  for (const tag of YEAR_ELEMENT_CANDIDATES) {
    const nodes = (root as Element).getElementsByTagNameNS?.("*", tag) ?? (root as Document).getElementsByTagNameNS("*", tag);
    for (let i = 0; i < nodes.length; i += 1) {
      const year = normaliseYear(nodes[i].textContent ?? "");
      if (year) return year;
    }
  }
  return undefined;
};

// Walks the ZIP for a single XML file matching `predicate(relativePath)`. The
// first match wins. Returns both the path (handy for warnings) and the parsed
// text content.
export const findXmlInZip = async (jszip: JSZip, predicate: (relPath: string) => boolean): Promise<{ path: string; xml: string } | undefined> => {
  let path: string | undefined;
  jszip.forEach((relPath: string, file: JSZip.JSZipObject) => {
    if (file.dir) return;
    if (path) return;
    if (predicate(relPath)) path = relPath;
  });
  if (!path) return undefined;
  const xml = await jszip.file(path)!.async("string");
  return { path, xml };
};
